import { useState, useCallback, useEffect } from 'react'
import { ELEMENTS, RARITY_CONFIG } from '../types'
import type { Creature, CreatureRarity } from '../types'
import { CreatureAPI } from '../api/client'
import { useAuth } from '../store/auth'
import CreatureDetail from '../components/ui/CreatureDetail'
import { useToast } from '../components/ui/Toast'
import { playSFX } from '../lib/audio'

type TabId = 'browse' | 'mine'

const ELEMENT_OPTS = [
  { label: 'All',    value: '' },
  { label: '🔥',    value: 'fire' },
  { label: '💧',    value: 'water' },
  { label: '🌿',    value: 'earth' },
  { label: '🌬️',   value: 'air' },
  { label: '🌑',    value: 'void' },
  { label: '✨',    value: 'aether' },
]

const RARITY_OPTS: { label: string; value: string }[] = [
  { label: 'All Rarities', value: '' },
  { label: 'Drift',  value: 'drift' },
  { label: 'Surge',  value: 'surge' },
  { label: 'Rift',   value: 'rift' },
  { label: 'Apex',   value: 'apex' },
  { label: 'Mythic', value: 'mythic' },
]

const RARITY_COLORS = {
  drift: '#8A8AA5',
  surge: '#00F0FF',
  rift: '#39FF14',
  apex: '#BF00FF',
  mythic: '#FFE800',
}

function MarketSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <div key={i} className="rounded-xl border p-4 animate-pulse" style={{ background: 'rgba(13,13,21,0.95)', borderColor: 'rgba(58,58,90,0.5)' }}>
          <div className="w-full h-20 rounded-lg mb-3" style={{ background: 'rgba(58,58,90,0.3)' }} />
          <div className="h-3 rounded w-3/4 mb-2" style={{ background: 'rgba(58,58,90,0.3)' }} />
          <div className="h-2 rounded w-1/2 mb-3" style={{ background: 'rgba(58,58,90,0.3)' }} />
          <div className="flex justify-between">
            <div className="h-8 rounded w-16" style={{ background: 'rgba(58,58,90,0.3)' }} />
            <div className="h-8 rounded w-12" style={{ background: 'rgba(58,58,90,0.3)' }} />
          </div>
        </div>
      ))}
    </div>
  )
}

const ELEMENT_ICONS: Record<string, string> = {
  fire: '/assets/icons/element-fire.png',
  water: '/assets/icons/element-water.png',
  earth: '/assets/icons/element-earth.png',
  air: '/assets/icons/element-air.png',
  void: '/assets/icons/element-void.png',
  aether: '/assets/icons/element-aether.png',
}

function ListingCard({ creature, isMine, onSelect }: {
  creature: Creature; isMine: boolean; onSelect: (c: Creature) => void
}) {
  const el = ELEMENTS[creature.element]
  const rarity = RARITY_CONFIG[creature.rarity]
  const conditionColor = creature.condition >= 80 ? '#39FF14' : creature.condition >= 50 ? '#00F0FF' : '#FF4800'
  const mockPrice = Math.round((rarity.weight > 0 ? (100 / rarity.weight) * 200 : 5000) * (creature.condition / 100))
  const rarityColor = RARITY_COLORS[creature.rarity as keyof typeof RARITY_COLORS]

  return (
    <div
      onClick={() => onSelect(creature)}
      className="group relative overflow-hidden rounded-xl border cursor-pointer transition-all duration-240 hover:scale-105"
      style={{
        background: 'rgba(13,13,21,0.95)',
        borderColor: `${rarityColor}40`,
        boxShadow: `0 0 12px ${rarityColor}15`,
      }}
    >
      {/* Rarity glow effect */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-240 pointer-events-none"
        style={{ background: `radial-gradient(circle at top right, ${rarityColor}15, transparent 70%)` }}
      />
      
      <div className="relative z-10 p-4">
        {/* Rarity badge */}
        <div className="absolute top-2 right-2">
          <span 
            className="px-2 py-0.5 rounded-full text-[8px] font-bold uppercase"
            style={{ 
              color: rarityColor,
              background: `${rarityColor}20`,
              border: `1px solid ${rarityColor}40`,
              fontFamily: 'Space Mono, monospace',
            }}
          >
            {rarity.label}
          </span>
        </div>

        {/* Creature icon */}
        <div
          className="w-16 h-16 rounded-xl flex items-center justify-center mb-3 mx-auto"
          style={{ 
            background: `${el.color}15`,
            border: `1px solid ${el.color}30`,
            boxShadow: `0 0 12px ${el.color}20`,
          }}
        >
          <img src={ELEMENT_ICONS[creature.element]} alt={el.label} className="w-10 h-10" />
        </div>

        {/* Name */}
        <h3 className="font-bold text-sm text-[#EAF6FF] uppercase tracking-tight text-center mb-1" style={{ fontFamily: 'Rajdhani, system-ui' }}>
          {creature.name}
        </h3>
        <p className="text-[10px] text-[#8A8AA5] text-center mb-3 truncate" style={{ fontFamily: 'Inter, sans-serif' }}>{creature.species}</p>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          {[
            { label: 'HP', value: `${creature.condition}%`, color: conditionColor },
            { label: 'Bond', value: `${creature.bondLevel}`, color: el.color },
            { label: 'Evo', value: `${creature.evolutionStage}/3`,  color: undefined },
          ].map(s => (
            <div key={s.label} className="text-center rounded-lg p-1.5" style={{ background: 'rgba(255,255,255,0.03)' }}>
              <p className="text-[8px] text-[#5A5A7A] uppercase tracking-wider" style={{ fontFamily: 'Space Mono, monospace' }}>{s.label}</p>
              <p className="font-bold text-[10px]" style={{ color: s.color ?? '#8BA0B4', fontFamily: 'Rajdhani, system-ui' }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Price + action */}
        <div className="flex items-center justify-between pt-2 border-t border-white border-opacity-5">
          <div>
            <p className="font-bold text-sm" style={{ color: '#FFE800', fontFamily: 'Rajdhani, system-ui' }}>⚡ {mockPrice.toLocaleString()}</p>
          </div>
          <button
            onClick={e => { e.stopPropagation(); playSFX(isMine ? 'click' : 'coin-collect'); onSelect(creature) }}
            className={`px-3 py-1.5 rounded-lg font-bold text-[10px] uppercase tracking-wider transition-all ${
              isMine 
                ? 'bg-[#3A3A5A] text-[#8A8AA5] hover:bg-[#5A5A7A]' 
                : 'bg-[#00F0FF] text-[#0A0A12] hover:bg-[#39FF14]'
            }`}
            style={{ fontFamily: 'Space Mono, monospace' }}
          >
            {isMine ? 'Unlist' : 'Buy'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Marketplace() {
  const { player } = useAuth()
  const { error: toastError } = useToast()
  const [tab, setTab] = useState<TabId>('browse')
  const [listings, setListings] = useState<Creature[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [apiError, setApiError] = useState<string | null>(null)
  const [isOffline, setIsOffline] = useState(false)
  const [selected, setSelected] = useState<Creature | null>(null)
  const [elementFilter, setElementFilter] = useState('')
  const [rarityFilter, setRarityFilter] = useState('')
  const [zoneFilter, setZoneFilter] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setApiError(null)
    setIsOffline(false)
    const res = await CreatureAPI.getMarketplace({
      ...(elementFilter && { element: elementFilter }),
      ...(rarityFilter && { rarity: rarityFilter }),
    })
    if (res.success && res.data) {
      setListings(res.data)
    } else {
      setApiError(res.error ?? 'Failed to load marketplace')
      if (!navigator.onLine) {
        setIsOffline(true)
        toastError('You\'re offline — showing cached listings')
      } else {
        toastError('Marketplace unreachable — check your connection')
      }
    }
    setLoading(false)
  }, [elementFilter, rarityFilter, toastError])

  useEffect(() => { load() }, [load])

  const allListings = listings ?? []
  const myListings = allListings.filter(c => c.bondedCharacterId === player?.id || c.isTrading)

  const displayListings = (tab === 'mine' ? myListings : allListings).filter(c => {
    if (zoneFilter && c.zone !== zoneFilter) return false
    return true
  })

  const hasFilters = elementFilter || rarityFilter || zoneFilter

  return (
    <div className="flex-1 flex flex-col" style={{ minHeight: '100vh', background: '#020510' }}>

      {/* Top Bar - Fortnite Style */}
      <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'rgba(58,58,90,0.5)', background: 'linear-gradient(135deg, rgba(0,240,255,0.05), rgba(191,0,255,0.05))' }}>
        <div className="flex items-center gap-3">
          <h1 className="font-black text-2xl uppercase tracking-wider" style={{ color: '#EAF6FF', fontFamily: 'Rajdhani, system-ui' }}>MARKETPLACE</h1>
          {isOffline && (
            <span className="font-bold text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-wider"
                  style={{ borderColor: '#BF00FF60', color: '#BF00FF', background: '#BF00FF10', fontFamily: 'Space Mono, monospace' }}>
              📵 CACHED
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading} className="w-10 h-10 rounded-lg flex items-center justify-center transition-all active:scale-95 font-bold text-lg"
                  style={{ background: 'rgba(13,13,21,0.8)', border: '1px solid rgba(58,58,90,0.5)', color: '#00F0FF' }}>
            {loading ? '⌛' : '↺'}
          </button>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border"
               style={{ background: 'rgba(13,13,21,0.8)', borderColor: 'rgba(58,58,90,0.5)' }}>
            <div className={`w-2 h-2 rounded-full ${apiError ? 'bg-[#FF4800]' : 'bg-[#00F0FF] animate-pulse'}`} />
            <span className="font-bold text-xs text-[#8A8AA5] uppercase" style={{ fontFamily: 'Space Mono, monospace' }}>{loading ? '—' : `${allListings.length} listed`}</span>
          </div>
        </div>
      </div>

      {/* Browse / My Listings tabs */}
      <div className="flex gap-1 p-2 border-b" style={{ borderColor: 'rgba(58,58,90,0.3)' }}>
        {([{ id: 'browse', label: 'Browse' }, { id: 'mine', label: 'My Listings' }] as const).map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 font-bold text-xs px-4 py-2 rounded-lg transition-all cursor-pointer ${
              tab === t.id
                ? 'text-[#020510] border border-[#00F0FF] bg-[#00F0FF]'
                : 'text-[#8A8AA5] border border-transparent hover:text-[#EAF6FF]'
            }`}
            style={{ fontFamily: 'Space Mono, monospace' }}
          >
            {t.label}
            {t.id === 'mine' && myListings.length > 0 && (
              <span className="ml-1.5 font-black text-[10px] text-[#FFE800]">{myListings.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Filters — only shown on Browse tab */}
      {tab === 'browse' && (
        <div className="space-y-2 px-4 py-3 border-b" style={{ borderColor: 'rgba(58,58,90,0.3)' }}>
          {/* Element */}
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
            {ELEMENT_OPTS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setElementFilter(opt.value)}
                className={`font-bold text-xs px-3 py-1.5 rounded-lg border transition-all bg-transparent cursor-pointer whitespace-nowrap ${
                  elementFilter === opt.value
                    ? 'text-[#020510] border-[#FFE800] bg-[#FFE800]'
                    : 'border-white border-opacity-10 text-[#8A8AA5] hover:border-opacity-20 hover:text-[#EAF6FF]'
                }`}
                style={{ fontFamily: 'Space Mono, monospace' }}
              >
                {opt.label || 'All'}
              </button>
            ))}
          </div>
          {/* Rarity */}
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
            {RARITY_OPTS.map(opt => {
              const cfg = opt.value ? RARITY_CONFIG[opt.value as CreatureRarity] : null
              const rarityColor = cfg ? RARITY_COLORS[opt.value as keyof typeof RARITY_COLORS] : '#8A8AA5'
              return (
                <button
                  key={opt.value}
                  onClick={() => setRarityFilter(opt.value)}
                  className={`font-bold text-[10px] tracking-widest uppercase px-2.5 py-1 rounded-lg border transition-all bg-transparent cursor-pointer whitespace-nowrap ${
                    rarityFilter === opt.value ? 'text-[#020510]' : 'border-white border-opacity-10 text-[#8A8AA5] hover:border-opacity-20'
                  }`}
                  style={rarityFilter === opt.value ? {
                    borderColor: rarityColor,
                    color: '#020510',
                    background: rarityColor,
                  } : undefined}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <MarketSkeleton />
        ) : apiError && displayListings.length === 0 ? (
          <div className="rounded-xl border p-8 text-center border-dashed" style={{ background: 'rgba(13,13,21,0.95)', borderColor: 'rgba(58,58,90,0.5)' }}>
            <p className="text-3xl mb-3">📡</p>
            <p className="font-bold text-[#EAF6FF] mb-2" style={{ fontFamily: 'Rajdhani, system-ui' }}>Marketplace Unreachable</p>
            <p className="text-sm text-[#8A8AA5] mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>{apiError}</p>
            <button onClick={load} className="px-4 py-2 rounded-lg font-bold uppercase tracking-wider text-sm transition-all hover:opacity-80"
                    style={{ background: '#FFE800', color: '#020510', fontFamily: 'Space Mono, monospace' }}>
              Retry
            </button>
          </div>
        ) : tab === 'mine' && displayListings.length === 0 ? (
          <div className="rounded-xl border p-8 text-center" style={{ background: 'rgba(13,13,21,0.95)', borderColor: 'rgba(58,58,90,0.5)' }}>
            <p className="text-3xl mb-3">📦</p>
            <p className="font-bold text-[#EAF6FF] mb-2" style={{ fontFamily: 'Rajdhani, system-ui' }}>No active listings</p>
            <p className="text-sm text-[#8A8AA5]" style={{ fontFamily: 'Inter, sans-serif' }}>
              List creatures from the Beast Vault to appear here.
            </p>
          </div>
        ) : displayListings.length === 0 ? (
          <div className="rounded-xl border p-8 text-center" style={{ background: 'rgba(13,13,21,0.95)', borderColor: 'rgba(58,58,90,0.5)' }}>
            <p className="text-3xl mb-3">⚖️</p>
            <p className="font-bold text-[#EAF6FF] mb-2" style={{ fontFamily: 'Rajdhani, system-ui' }}>No listings match</p>
            <p className="text-sm text-[#8A8AA5]" style={{ fontFamily: 'Inter, sans-serif' }}>
              {hasFilters ? 'Try clearing your filters.' : 'The marketplace opens when players start trading.'}
            </p>
            {hasFilters && (
              <button
                onClick={() => { setElementFilter(''); setRarityFilter(''); setZoneFilter('') }}
                className="px-4 py-2 rounded-lg font-bold uppercase tracking-wider text-sm transition-all hover:opacity-80 mt-4"
                style={{ background: 'rgba(58,58,90,0.3)', color: '#8A8AA5', fontFamily: 'Space Mono, monospace' }}
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {displayListings.map(creature => (
              <ListingCard
                key={creature.id}
                creature={creature}
                isMine={tab === 'mine'}
                onSelect={setSelected}
              />
            ))}
          </div>
        )}

        {/* P2P note */}
        <div className="rounded-xl border p-3 mt-4 text-center" style={{ background: 'rgba(13,13,21,0.95)', borderColor: 'rgba(58,58,90,0.5)' }}>
          <p className="text-[10px] text-[#FFE800] mb-1 uppercase tracking-wider" style={{ fontFamily: 'Space Mono, monospace' }}>⚡ P2P Trading</p>
          <p className="text-xs text-[#8A8AA5]" style={{ fontFamily: 'Inter, sans-serif' }}>
            Direct trades and auction house coming in Phase 2. Only Stage 1–2 creatures are tradeable.
          </p>
        </div>
      </div>

      <CreatureDetail creature={selected} onClose={() => setSelected(null)} />
    </div>
  )
}

